/**
 * City Agency Landing Pages - Wave 2
 * Adds: Copenhagen, Oslo, Helsinki, Dublin, Zurich, Singapore, Seoul, Sydney, Toronto, Mexico City
 */
import type { CityAgencyData } from './city-agency-pages';

const sharedServices = [
  { name: 'Custom SaaS Development', description: 'End-to-end SaaS product development: architecture, development, deployment, and scaling.' },
  { name: 'Web Application Development', description: 'Enterprise web apps, portals, dashboards. React, TypeScript, cloud-native.' },
  { name: 'Mobile App Development', description: 'Native iOS/Android and cross-platform with React Native and Flutter.' },
  { name: 'AI & Machine Learning', description: 'NLP, computer vision, predictive analytics, LLM integration.' },
  { name: 'Cloud & DevOps', description: 'AWS, Azure, GCP. CI/CD, containers, monitoring, managed services.' },
  { name: 'Staff Augmentation', description: 'Dedicated developers, QA, DevOps integrated into your team.' },
  { name: 'UI/UX Design', description: 'User research, wireframing, prototyping, design systems.' },
  { name: 'Cybersecurity', description: 'Pen testing, security audits, SOC, compliance (ISO 27001, GDPR, HIPAA).' },
];

const sharedIndustries = [
  { name: 'FinTech & Banking', description: 'Payment platforms, trading systems, compliance tools.' },
  { name: 'E-commerce & Retail', description: 'Online stores, marketplace platforms, omnichannel solutions.' },
  { name: 'Healthcare & MedTech', description: 'Patient portals, telemedicine, EHR integrations.' },
  { name: 'SaaS & Technology', description: 'Product development and scaling for SaaS companies.' },
  { name: 'Real Estate & PropTech', description: 'Property management, virtual tours, tenant portals.' },
  { name: 'Logistics & Supply Chain', description: 'Fleet management, route optimization, warehouse systems.' },
];

export const CITY_AGENCY_PAGES_WAVE2: Record<string, CityAgencyData> = {

  copenhagen: {
    slug: 'software-development-agency-copenhagen',
    cityName: 'Copenhagen',
    countryName: 'Denmark',
    flag: '🇩🇰',
    timezone: 'CET (GMT+1)',
    currency: 'DKK/EUR',
    title: {
      en: 'Software Development Agency Copenhagen | Call IT Dev',
      da: 'Softwareudviklingsbureau København | Call IT Dev',
      fr: 'Agence de Développement Informatique Copenhague | Call IT Dev',
      de: 'Softwareentwicklung Agentur Kopenhagen | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Copenhagen, Denmark. Custom SaaS, web, mobile, AI. CET timezone, from €25/hr. 55% savings vs Copenhagen.',
      da: 'Softwareudviklingsbureau for København. SaaS, web, mobil, AI. CET-tidszone, fra 25 €/t. 55% besparelse vs København.',
      fr: 'Agence de développement informatique pour Copenhague. SaaS, web, mobile, IA. Fuseau CET, dès 25 €/h. 55% d\'économies.',
      de: 'Softwareentwicklungsagentur für Kopenhagen. SaaS, Web, Mobile, KI. CET-Zeitzone, ab 25 €/h. 55% Ersparnis.',
    },
    keywords: ['software development agency Copenhagen', 'IT outsourcing Copenhagen', 'softwareudvikling København', 'nearshore development Denmark'],
    heroTitle: { en: 'Software Development Agency for Copenhagen', da: 'Softwareudviklingsbureau for København', fr: 'Agence de Développement pour Copenhague' },
    heroSubtitle: { en: 'CET-aligned engineers at nearshore rates. 55% savings vs Copenhagen agencies.', da: 'CET-tilpassede ingeniører til nearshore-priser. 55% besparelse vs københavnske bureauer.', fr: 'Ingénieurs en fuseau CET à tarifs nearshore. 55% d\'économies.' },
    whyTitle: { en: 'Why Copenhagen Companies Choose Call IT Dev', da: 'Hvorfor Københavnske Virksomheder Vælger Call IT Dev', fr: 'Pourquoi les Entreprises de Copenhague Choisissent Call IT Dev' },
    whyReasons: [
      { icon: '⏰', title: 'Same CET Timezone', description: 'Full-day overlap with Copenhagen for Agile collaboration.' },
      { icon: '💰', title: '55% Cost Savings', description: 'Senior devs at €25-40/hr vs €70-100/hr in Copenhagen.' },
      { icon: '🗣️', title: 'English-Fluent Teams', description: 'All communication in English — the business language of Danish tech.' },
      { icon: '🚀', title: 'Startup-Friendly', description: 'Experience with Copenhagen\'s vibrant startup ecosystem.' },
      { icon: '🛡️', title: 'GDPR Compliant', description: 'Enterprise security standards, fully GDPR/Datatilsynet compliant.' },
      { icon: '📈', title: 'Scalable Teams', description: 'Scale from 2 to 50+ developers rapidly.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'How does nearshore compare to Copenhagen rates?', answer: 'Senior devs in Copenhagen cost €70-100/hr. Our rate is €25-40/hr — 55% savings, same CET timezone.' },
      { question: 'Are you GDPR compliant?', answer: 'Yes. Enterprise security standards, fully compliant with Danish GDPR and Datatilsynet requirements.' },
      { question: 'Do you work with Danish startups?', answer: 'Yes! Flexible models for seed to Series B. No long-term lock-in.' },
      { question: 'How quickly can you start?', answer: 'Team setup in 2 weeks. CVs within 5 business days.' },
      { question: 'What technologies do you use?', answer: 'React, TypeScript, Node.js, Python, Java, .NET, AWS, Azure, GCP, Docker, Kubernetes.' },
      { question: 'Do your engineers speak Danish?', answer: 'Communication is in English (the business language of Danish tech). Danish-speaking PMs available for select projects.' },
    ],
    localRate: '€70-100',
    ourRate: '€25-40',
    savings: '55%+',
  },

  oslo: {
    slug: 'software-development-agency-oslo',
    cityName: 'Oslo',
    countryName: 'Norway',
    flag: '🇳🇴',
    timezone: 'CET (GMT+1)',
    currency: 'NOK/EUR',
    title: {
      en: 'Software Development Agency Oslo | Call IT Dev',
      sv: 'Mjukvaruutveckling Oslo | Call IT Dev',
      fr: 'Agence de Développement Informatique Oslo | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Oslo, Norway. Custom SaaS, web, mobile, AI. CET timezone, from €25/hr. 60% savings vs Oslo agencies.',
      sv: 'Mjukvaruutvecklingsagentur för Oslo. SaaS, webb, mobil, AI. CET-tidszon, från 25 €/t. 60% besparing.',
      fr: 'Agence de développement informatique pour Oslo. SaaS, web, mobile, IA. Fuseau CET, dès 25 €/h. 60% d\'économies.',
    },
    keywords: ['software development agency Oslo', 'IT outsourcing Oslo', 'nearshore development Norway', 'programvareutvikling Oslo'],
    heroTitle: { en: 'Software Development Agency for Oslo', fr: 'Agence de Développement pour Oslo' },
    heroSubtitle: { en: 'CET-aligned engineers at competitive rates. 60% savings vs Oslo. Same timezone, no communication barriers.', fr: 'Ingénieurs CET à tarifs compétitifs. 60% d\'économies vs Oslo.' },
    whyTitle: { en: 'Why Oslo Companies Choose Call IT Dev', fr: 'Pourquoi les Entreprises d\'Oslo Choisissent Call IT Dev' },
    whyReasons: [
      { icon: '⏰', title: 'Same CET Timezone', description: 'Full overlap with Norwegian business hours.' },
      { icon: '💰', title: '60% Cost Savings', description: 'Norway has the highest dev salaries in Europe. We offer senior devs at €25-40/hr vs €80-120/hr.' },
      { icon: '🗣️', title: 'English-Fluent', description: 'All engineers fluent in English — Norway\'s tech lingua franca.' },
      { icon: '🛡️', title: 'GDPR Compliant', description: 'ISO 27001, fully GDPR/Datatilsynet compliant.' },
      { icon: '🚀', title: 'Energy & Maritime', description: 'Experience with Norway\'s energy and maritime tech sectors.' },
      { icon: '📈', title: 'Scale Fast', description: 'From 2 to 50+ engineers. Monthly billing.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'How does nearshore compare to Oslo rates?', answer: 'Norway has the highest developer costs in Europe (€80-120/hr). Our rate is €25-40/hr — 60%+ savings.' },
      { question: 'Are you GDPR compliant?', answer: 'Yes. Enterprise security standards, fully GDPR compliant.' },
      { question: 'Do you have experience with energy sector projects?', answer: 'Yes. We work with energy, maritime, and oil & gas tech companies.' },
      { question: 'How quickly can you start?', answer: 'Team setup in 2 weeks.' },
      { question: 'What is the minimum team size?', answer: 'Start with 2 developers, scale as needed.' },
      { question: 'What technologies do you use?', answer: 'React, TypeScript, Node.js, Python, Java, .NET, AWS, Azure, GCP.' },
    ],
    localRate: '€80-120',
    ourRate: '€25-40',
    savings: '60%+',
  },

  helsinki: {
    slug: 'software-development-agency-helsinki',
    cityName: 'Helsinki',
    countryName: 'Finland',
    flag: '🇫🇮',
    timezone: 'EET (GMT+2)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Helsinki | Call IT Dev',
      fr: 'Agence de Développement Informatique Helsinki | Call IT Dev',
      sv: 'Mjukvaruutveckling Helsingfors | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Helsinki, Finland. Custom SaaS, web, mobile, AI. 1h time diff, from €25/hr. 55% savings vs Helsinki.',
      fr: 'Agence de développement pour Helsinki. SaaS, web, mobile, IA. 1h de décalage, dès 25 €/h. 55% d\'économies.',
      sv: 'Mjukvaruutvecklingsagentur för Helsingfors. SaaS, webb, mobil, AI. 1h tidsskillnad, från 25 €/t.',
    },
    keywords: ['software development agency Helsinki', 'IT outsourcing Helsinki', 'nearshore development Finland', 'ohjelmistokehitys Helsinki'],
    heroTitle: { en: 'Software Development Agency for Helsinki', fr: 'Agence de Développement pour Helsinki' },
    heroSubtitle: { en: '1-hour time difference, English-fluent engineers. 55% savings vs Helsinki agencies.', fr: '1h de décalage, ingénieurs anglophones. 55% d\'économies.' },
    whyTitle: { en: 'Why Helsinki Companies Choose Call IT Dev', fr: 'Pourquoi les Entreprises d\'Helsinki Choisissent Call IT Dev' },
    whyReasons: [
      { icon: '⏰', title: '1h Time Difference', description: 'CET vs EET — only 1 hour apart. Near-full overlap.' },
      { icon: '💰', title: '55% Cost Savings', description: 'Senior devs at €25-40/hr vs €65-95/hr in Helsinki.' },
      { icon: '🗣️', title: 'English-Fluent', description: 'All communication in English — Finland\'s tech language.' },
      { icon: '🎮', title: 'Gaming Expertise', description: 'Experience with gaming and mobile game development — Finland\'s strength.' },
      { icon: '🛡️', title: 'GDPR Compliant', description: 'Enterprise security standards, EU GDPR compliant.' },
      { icon: '📈', title: 'Scalable', description: 'Agile teams from 2 to 40+ engineers.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'How does nearshore compare to Helsinki rates?', answer: 'Senior devs in Helsinki cost €65-95/hr. Our rate is €25-40/hr — 55% savings with 1h time difference.' },
      { question: 'Do you have gaming development experience?', answer: 'Yes! Mobile games, Unity, Unreal Engine, backend services for gaming platforms.' },
      { question: 'Are you GDPR compliant?', answer: 'Yes. Enterprise security standards, fully EU GDPR compliant.' },
      { question: 'How quickly can you start?', answer: 'Team setup in 2 weeks.' },
      { question: 'What is the minimum team size?', answer: 'Start with 2 developers.' },
      { question: 'What technologies do you use?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, GCP, Unity, Unreal.' },
    ],
    localRate: '€65-95',
    ourRate: '€25-40',
    savings: '55%+',
  },

  dublin: {
    slug: 'software-development-agency-dublin',
    cityName: 'Dublin',
    countryName: 'Ireland',
    flag: '🇮🇪',
    timezone: 'GMT/IST',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Dublin | Call IT Dev',
      fr: 'Agence de Développement Informatique Dublin | Call IT Dev',
      de: 'Softwareentwicklung Agentur Dublin | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Dublin, Ireland. Custom SaaS, web, mobile, AI. English-native teams, 1h time diff, from €25/hr. 55% savings vs Dublin.',
      fr: 'Agence de développement pour Dublin. SaaS, web, mobile, IA. Équipes anglophones, dès 25 €/h. 55% d\'économies.',
      de: 'Softwareentwicklungsagentur für Dublin. SaaS, Web, Mobile, KI. Englischsprachige Teams, ab 25 €/h.',
    },
    keywords: ['software development agency Dublin', 'IT outsourcing Dublin', 'nearshore development Ireland', 'web development Dublin'],
    heroTitle: { en: 'Software Development Agency for Dublin', fr: 'Agence de Développement pour Dublin' },
    heroSubtitle: { en: 'English-fluent engineers at nearshore rates. 1h time difference, 55% savings vs Dublin agencies.', fr: 'Ingénieurs anglophones à tarifs nearshore. 1h de décalage, 55% d\'économies.' },
    whyTitle: { en: 'Why Dublin Companies Choose Call IT Dev', fr: 'Pourquoi les Entreprises de Dublin Choisissent Call IT Dev' },
    whyReasons: [
      { icon: '⏰', title: '1h Time Difference', description: 'Morocco GMT+1, Dublin GMT/IST. Near-full working day overlap.' },
      { icon: '💰', title: '55% Cost Savings', description: 'Senior devs at €25-40/hr vs €70-100/hr in Dublin.' },
      { icon: '🗣️', title: 'English-Fluent', description: 'All engineers fluent in English — perfect for Dublin\'s tech ecosystem.' },
      { icon: '🏢', title: 'EMEA HQ Expertise', description: 'Experience working with companies that have European HQs in Dublin.' },
      { icon: '🛡️', title: 'GDPR/DPC Compliant', description: 'Enterprise security standards, compliant with Irish DPC and EU GDPR.' },
      { icon: '🚀', title: 'Tech Talent Hub', description: '200+ engineers for rapid team scaling.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'How does nearshore compare to Dublin?', answer: 'Senior devs in Dublin cost €70-100/hr. Our rate is €25-40/hr — 55% savings with 1h time difference.' },
      { question: 'Are you GDPR/DPC compliant?', answer: 'Yes. ISO 27001, fully compliant with Irish Data Protection Commission and EU GDPR.' },
      { question: 'Do you work with Dublin tech companies?', answer: 'Yes — SaaS, fintech, and enterprise companies with European HQs in Dublin.' },
      { question: 'How quickly can you start?', answer: 'Team setup in 2 weeks.' },
      { question: 'What is the minimum team size?', answer: 'Start with 2 developers, scale to 30+.' },
      { question: 'What technologies do you use?', answer: 'React, TypeScript, Node.js, Python, Java, .NET, AWS, Azure, GCP.' },
    ],
    localRate: '€70-100',
    ourRate: '€25-40',
    savings: '55%+',
  },

  zurich: {
    slug: 'software-development-agency-zurich',
    cityName: 'Zurich',
    countryName: 'Switzerland',
    flag: '🇨🇭',
    timezone: 'CET (GMT+1)',
    currency: 'CHF',
    title: {
      en: 'Software Development Agency Zurich | Call IT Dev',
      de: 'Softwareentwicklung Agentur Zürich | Call IT Dev',
      fr: 'Agence de Développement Informatique Zurich | Call IT Dev',
      it: 'Agenzia Sviluppo Software Zurigo | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Zurich, Switzerland. Custom SaaS, web, mobile, AI. Multilingual teams DE/FR/IT, CET, from CHF 30/hr. 65% savings.',
      de: 'Softwareentwicklungsagentur für Zürich. SaaS, Web, Mobile, KI. Mehrsprachige Teams DE/FR/IT, CET, ab CHF 30/h. 65% Ersparnis.',
      fr: 'Agence de développement pour Zurich. SaaS, web, mobile, IA. Équipes multilingues DE/FR/IT, CET, dès 30 CHF/h. 65% d\'économies.',
      it: 'Agenzia di sviluppo software per Zurigo. SaaS, web, mobile, IA. Team multilingue DE/FR/IT, CET, da 30 CHF/h.',
    },
    keywords: ['software development agency Zurich', 'Softwareentwicklung Zürich', 'IT outsourcing Zurich', 'nearshore development Switzerland'],
    heroTitle: { en: 'Software Development Agency for Zurich', de: 'Softwareentwicklung Agentur für Zürich', fr: 'Agence de Développement pour Zurich' },
    heroSubtitle: { en: 'Trilingual DE/FR/IT engineers at nearshore rates. Same CET timezone, 65% savings vs Zurich.', de: 'Dreisprachige DE/FR/IT Ingenieure zu Nearshore-Preisen. Gleiche CET-Zeitzone, 65% Ersparnis.', fr: 'Ingénieurs trilingues DE/FR/IT. Même fuseau CET, 65% d\'économies.' },
    whyTitle: { en: 'Why Zurich Companies Choose Call IT Dev', de: 'Warum Zürcher Unternehmen Call IT Dev Wählen', fr: 'Pourquoi les Entreprises de Zurich Choisissent Call IT Dev' },
    whyReasons: [
      { icon: '⏰', title: 'Same CET Timezone', description: 'Identical working hours with Zurich.' },
      { icon: '💰', title: '65% Cost Savings', description: 'Switzerland has the highest dev rates globally. We offer €25-40/hr vs CHF 120-180/hr.' },
      { icon: '🗣️', title: 'Trilingual Teams', description: 'Engineers who speak French, German, and English — matching Swiss needs.' },
      { icon: '🏦', title: 'FinTech Expertise', description: 'Experience with Swiss banking and fintech compliance.' },
      { icon: '🛡️', title: 'FADP/GDPR Compliant', description: 'ISO 27001, Swiss FADP and EU GDPR compliant.' },
      { icon: '✈️', title: '3h Direct Flight', description: 'Casablanca-Zurich: direct flights available.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'How does nearshore compare to Zurich?', answer: 'Senior devs in Zurich cost CHF 120-180/hr. Our rate is CHF 30-50/hr — 65% savings, same CET timezone.' },
      { question: 'Do your engineers speak German and French?', answer: 'Yes. We have trilingual teams (French/German/English) matching Swiss requirements.' },
      { question: 'Are you compliant with Swiss FADP?', answer: 'Yes. Enterprise security standards, compliant with Swiss Federal Act on Data Protection and EU GDPR.' },
      { question: 'Do you work with Swiss banks?', answer: 'Yes — FinTech, banking platforms, and compliance tools with appropriate security certifications.' },
      { question: 'How quickly can you start?', answer: 'Team setup in 2 weeks.' },
      { question: 'What is the minimum team?', answer: 'Start with 2 developers, scale to 50+.' },
    ],
    localRate: 'CHF 120-180',
    ourRate: 'CHF 30-50',
    savings: '65%+',
  },

  singapore: {
    slug: 'software-development-agency-singapore',
    cityName: 'Singapore',
    countryName: 'Singapore',
    flag: '🇸🇬',
    timezone: 'SGT (GMT+8)',
    currency: 'SGD/USD',
    title: {
      en: 'Software Development Agency Singapore | Call IT Dev',
      fr: 'Agence de Développement Informatique Singapour | Call IT Dev',
      zh: '新加坡软件开发公司 | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Singapore. Custom SaaS, web, mobile, AI. English-speaking teams, from $25/hr. 55% savings vs Singapore.',
      fr: 'Agence de développement pour Singapour. SaaS, web, mobile, IA. Équipes anglophones, dès 25 $/h. 55% d\'économies.',
      zh: '为新加坡提供软件开发服务。SaaS、Web、移动、AI。英语团队，25美元/小时起。节省55%。',
    },
    keywords: ['software development agency Singapore', 'IT outsourcing Singapore', 'web development Singapore', 'nearshore development APAC'],
    heroTitle: { en: 'Software Development Agency for Singapore', fr: 'Agence de Développement pour Singapour', zh: '新加坡软件开发公司' },
    heroSubtitle: { en: 'English-fluent engineers at competitive rates. Follow-the-sun development with 55% savings.', fr: 'Ingénieurs anglophones à tarifs compétitifs. 55% d\'économies.' },
    whyTitle: { en: 'Why Singapore Companies Choose Call IT Dev', fr: 'Pourquoi les Entreprises de Singapour Choisissent Call IT Dev' },
    whyReasons: [
      { icon: '💰', title: '55% Cost Savings', description: 'Senior devs at $25-40/hr vs $60-100/hr in Singapore.' },
      { icon: '🗣️', title: 'English-Fluent', description: 'All engineers fluent English speakers — Singapore\'s business language.' },
      { icon: '⏰', title: 'Follow-the-Sun', description: 'GMT+1 complements GMT+8 for extended development hours.' },
      { icon: '🌏', title: 'APAC + EMEA Bridge', description: 'Serve both European and Asian markets from one partner.' },
      { icon: '🛡️', title: 'PDPA Aligned', description: 'ISO 27001, GDPR compliant — aligned with Singapore PDPA.' },
      { icon: '🚀', title: 'Scalable', description: 'From 2 to 50+ engineers. Flexible engagement.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'How does the timezone work with Singapore?', answer: 'Morocco GMT+1, Singapore GMT+8. Our afternoon is your morning. 3-4h daily overlap, plus async follow-the-sun delivery.' },
      { question: 'How does nearshore compare to Singapore?', answer: 'Senior devs in Singapore cost $60-100/hr. Our rate is $25-40/hr — 55% savings.' },
      { question: 'Are you PDPA compliant?', answer: 'Yes. Enterprise security standards, GDPR compliant, aligned with Singapore PDPA.' },
      { question: 'Do you work with APAC companies?', answer: 'Yes — SaaS, fintech, and enterprise companies across APAC.' },
      { question: 'How quickly can you start?', answer: 'Team setup in 2 weeks.' },
      { question: 'What technologies do you use?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, GCP.' },
    ],
    localRate: '$60-100',
    ourRate: '$25-40',
    savings: '55%+',
  },

  seoul: {
    slug: 'software-development-agency-seoul',
    cityName: 'Seoul',
    countryName: 'South Korea',
    flag: '🇰🇷',
    timezone: 'KST (GMT+9)',
    currency: 'KRW/USD',
    title: {
      en: 'Software Development Agency Seoul | Call IT Dev',
      ko: '서울 소프트웨어 개발 에이전시 | Call IT Dev',
      fr: 'Agence de Développement Informatique Séoul | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Seoul, South Korea. Custom SaaS, web, mobile, AI. English-speaking teams, from $25/hr. 55% savings.',
      ko: '서울을 위한 소프트웨어 개발 에이전시. SaaS, 웹, 모바일, AI. 영어 팀, 시간당 25달러부터. 55% 절감.',
      fr: 'Agence de développement pour Séoul. SaaS, web, mobile, IA. Équipes anglophones, dès 25 $/h.',
    },
    keywords: ['software development agency Seoul', 'IT outsourcing Seoul', '서울 소프트웨어 개발', 'nearshore development Korea'],
    heroTitle: { en: 'Software Development Agency for Seoul', ko: '서울을 위한 소프트웨어 개발 에이전시', fr: 'Agence de Développement pour Séoul' },
    heroSubtitle: { en: 'English-fluent engineers for Korean companies going global. Follow-the-sun delivery.', ko: '글로벌 진출을 위한 영어 능통 엔지니어 팀. 팔로우더선 개발.', fr: 'Ingénieurs anglophones pour entreprises coréennes. Livraison follow-the-sun.' },
    whyTitle: { en: 'Why Seoul Companies Choose Call IT Dev', ko: '서울 기업이 Call IT Dev를 선택하는 이유', fr: 'Pourquoi les Entreprises de Séoul Choisissent Call IT Dev' },
    whyReasons: [
      { icon: '💰', title: '55% Cost Savings', description: 'Senior devs at $25-40/hr vs $50-80/hr in Seoul.' },
      { icon: '🗣️', title: 'English-Fluent', description: 'Native English communication for global products.' },
      { icon: '⏰', title: 'Follow-the-Sun', description: 'GMT+1 + GMT+9 = nearly 24h dev coverage.' },
      { icon: '🌍', title: 'European Expertise', description: 'GDPR, EU market knowledge for Korean companies expanding West.' },
      { icon: '🛡️', title: 'PIPA Aligned', description: 'Enterprise security standards, GDPR compliant, aligned with Korean PIPA.' },
      { icon: '📈', title: 'Scalable', description: 'From 2 to 50+ engineers rapidly.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'How does the timezone work?', answer: 'Morocco GMT+1, Seoul GMT+9. Our morning overlaps with your late afternoon. Plus async follow-the-sun delivery.' },
      { question: 'How does nearshore compare to Seoul?', answer: 'Senior devs in Seoul cost $50-80/hr. Our rate is $25-40/hr — 55% savings.' },
      { question: 'Do you support Korean companies going global?', answer: 'Yes — European language support, GDPR expertise, and EU market knowledge.' },
      { question: 'How quickly can you start?', answer: 'Team setup in 2 weeks.' },
      { question: 'Are you PIPA compliant?', answer: 'Yes. Enterprise security standards, aligned with Korean Personal Information Protection Act.' },
      { question: 'What technologies do you use?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, GCP.' },
    ],
    localRate: '$50-80',
    ourRate: '$25-40',
    savings: '55%+',
  },

  sydney: {
    slug: 'software-development-agency-sydney',
    cityName: 'Sydney',
    countryName: 'Australia',
    flag: '🇦🇺',
    timezone: 'AEST (GMT+10)',
    currency: 'AUD',
    title: {
      en: 'Software Development Agency Sydney | Call IT Dev',
      fr: 'Agence de Développement Informatique Sydney | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Sydney, Australia. Custom SaaS, web, mobile, AI. English-speaking teams, from AUD 35/hr. 60% savings vs Sydney.',
      fr: 'Agence de développement pour Sydney. SaaS, web, mobile, IA. Équipes anglophones, dès 35 AUD/h. 60% d\'économies.',
    },
    keywords: ['software development agency Sydney', 'IT outsourcing Sydney', 'nearshore development Australia', 'web development Sydney'],
    heroTitle: { en: 'Software Development Agency for Sydney', fr: 'Agence de Développement pour Sydney' },
    heroSubtitle: { en: 'English-fluent engineers at competitive rates. Follow-the-sun development, 60% savings vs Sydney.', fr: 'Ingénieurs anglophones. Follow-the-sun, 60% d\'économies vs Sydney.' },
    whyTitle: { en: 'Why Sydney Companies Choose Call IT Dev', fr: 'Pourquoi les Entreprises de Sydney Choisissent Call IT Dev' },
    whyReasons: [
      { icon: '💰', title: '60% Cost Savings', description: 'Senior devs at AUD 35-55/hr vs AUD 100-160/hr in Sydney.' },
      { icon: '🗣️', title: 'English-Native', description: 'All engineers fluent English speakers.' },
      { icon: '⏰', title: 'Follow-the-Sun', description: 'GMT+1 + GMT+10 enables near-24h development cycles.' },
      { icon: '🛡️', title: 'Privacy Act Aligned', description: 'Enterprise security standards, GDPR compliant, aligned with Australian Privacy Act.' },
      { icon: '🚀', title: 'Startup to Enterprise', description: 'From 2 to 50+ engineers. Agile and enterprise models.' },
      { icon: '🌏', title: 'APAC + EMEA Bridge', description: 'Serve both hemispheres from a single nearshore partner.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'How does the timezone work with Sydney?', answer: 'Morocco GMT+1, Sydney GMT+10. 3-4 hours overlap in early morning. Plus productive async follow-the-sun delivery.' },
      { question: 'How does nearshore compare to Sydney?', answer: 'Senior devs in Sydney cost AUD 100-160/hr. Our rate is AUD 35-55/hr — 60% savings.' },
      { question: 'Are you compliant with Australian regulations?', answer: 'Yes. Enterprise security standards, aligned with Australian Privacy Act and Notifiable Data Breaches scheme.' },
      { question: 'How quickly can you start?', answer: 'Team setup in 2 weeks. CVs within 5 business days.' },
      { question: 'Do you work with Australian companies?', answer: 'Yes — SaaS, fintech, proptech, and e-commerce companies across Australia.' },
      { question: 'What technologies do you use?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, GCP.' },
    ],
    localRate: 'AUD 100-160',
    ourRate: 'AUD 35-55',
    savings: '60%+',
  },

  toronto: {
    slug: 'software-development-agency-toronto',
    cityName: 'Toronto',
    countryName: 'Canada',
    flag: '🇨🇦',
    timezone: 'EST (GMT-5)',
    currency: 'CAD',
    title: {
      en: 'Software Development Agency Toronto | Call IT Dev',
      fr: 'Agence de Développement Informatique Toronto | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Toronto, Canada. Custom SaaS, web, mobile, AI. Bilingual EN/FR teams, from CAD 35/hr. 55% savings vs Toronto.',
      fr: 'Agence de développement pour Toronto. SaaS, web, mobile, IA. Équipes bilingues EN/FR, dès 35 CAD/h. 55% d\'économies.',
    },
    keywords: ['software development agency Toronto', 'IT outsourcing Toronto', 'nearshore development Canada', 'web development Toronto'],
    heroTitle: { en: 'Software Development Agency for Toronto', fr: 'Agence de Développement pour Toronto' },
    heroSubtitle: { en: 'Bilingual English/French engineers. EST timezone overlap, 55% savings vs Toronto agencies.', fr: 'Ingénieurs bilingues anglais/français. Chevauchement EST, 55% d\'économies.' },
    whyTitle: { en: 'Why Toronto Companies Choose Call IT Dev', fr: 'Pourquoi les Entreprises de Toronto Choisissent Call IT Dev' },
    whyReasons: [
      { icon: '💰', title: '55% Cost Savings', description: 'Senior devs at CAD 35-55/hr vs CAD 80-130/hr in Toronto.' },
      { icon: '🗣️', title: 'Bilingual EN/FR', description: 'Ideal for Canadian companies with bilingual requirements.' },
      { icon: '⏰', title: 'EST Overlap', description: 'GMT+1 overlaps with Toronto morning hours for real-time collaboration.' },
      { icon: '🛡️', title: 'PIPEDA Aligned', description: 'Enterprise security standards, GDPR compliant, aligned with Canadian PIPEDA.' },
      { icon: '🚀', title: 'Tech Hub Expertise', description: 'Experience with Toronto\'s AI and fintech ecosystem.' },
      { icon: '📈', title: 'Scale Fast', description: 'From 2 to 50+ engineers rapidly.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'How does nearshore compare to Toronto?', answer: 'Senior devs in Toronto cost CAD 80-130/hr. Our rate is CAD 35-55/hr — 55% savings.' },
      { question: 'Do your engineers speak French?', answer: 'Yes! We have bilingual EN/FR teams — perfect for Canadian bilingual requirements.' },
      { question: 'How does the timezone work?', answer: 'Morocco GMT+1, Toronto GMT-5. 4-5h overlap during morning hours. Ideal for daily standups.' },
      { question: 'Are you PIPEDA compliant?', answer: 'Yes. Enterprise security standards, aligned with Canadian PIPEDA requirements.' },
      { question: 'How quickly can you start?', answer: 'Team setup in 2 weeks.' },
      { question: 'What technologies do you use?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, GCP.' },
    ],
    localRate: 'CAD 80-130',
    ourRate: 'CAD 35-55',
    savings: '55%+',
  },

  'mexico-city': {
    slug: 'software-development-agency-mexico-city',
    cityName: 'Mexico City',
    countryName: 'Mexico',
    flag: '🇲🇽',
    timezone: 'CST (GMT-6)',
    currency: 'MXN/USD',
    title: {
      en: 'Software Development Agency Mexico City | Call IT Dev',
      es: 'Agencia de Desarrollo de Software Ciudad de México | Call IT Dev',
      fr: 'Agence de Développement Informatique Mexico | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Mexico City. Custom SaaS, web, mobile, AI. Bilingual EN/ES teams, from $20/hr. 50% savings vs CDMX.',
      es: 'Agencia de desarrollo de software para Ciudad de México. SaaS, web, móvil, IA. Equipos bilingües EN/ES, desde 20 $/h. 50% ahorro.',
      fr: 'Agence de développement pour Mexico. SaaS, web, mobile, IA. Équipes bilingues EN/ES, dès 20 $/h. 50% d\'économies.',
    },
    keywords: ['software development agency Mexico City', 'agencia desarrollo software CDMX', 'IT outsourcing Mexico', 'nearshore development Mexico'],
    heroTitle: { en: 'Software Development Agency for Mexico City', es: 'Agencia de Desarrollo de Software para Ciudad de México', fr: 'Agence de Développement pour Mexico' },
    heroSubtitle: { en: 'Bilingual Spanish/English engineers. Competitive rates, 50% savings vs Mexico City agencies.', es: 'Ingenieros bilingües español/inglés. Precios competitivos, 50% ahorro vs agencias de CDMX.', fr: 'Ingénieurs bilingues espagnol/anglais. 50% d\'économies.' },
    whyTitle: { en: 'Why Mexico City Companies Choose Call IT Dev', es: 'Por Qué las Empresas de CDMX Eligen Call IT Dev', fr: 'Pourquoi les Entreprises de Mexico Choisissent Call IT Dev' },
    whyReasons: [
      { icon: '💰', title: '50% Cost Savings', description: 'Senior devs at $20-35/hr vs $40-70/hr in Mexico City.' },
      { icon: '🗣️', title: 'Bilingual EN/ES', description: 'Native Spanish + fluent English for pan-American projects.' },
      { icon: '⏰', title: 'Americas Overlap', description: 'GMT+1 overlaps with CDMX afternoon for daily sync.' },
      { icon: '🌎', title: 'LatAm + Europe Bridge', description: 'Serve both European and Latin American markets.' },
      { icon: '🛡️', title: 'LFPDPPP Aligned', description: 'Enterprise security standards, GDPR compliant, aligned with Mexican data protection.' },
      { icon: '📈', title: 'Scalable', description: 'From 2 to 50+ engineers. Flexible engagement.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'How does nearshore compare to CDMX?', answer: 'Senior devs in Mexico City cost $40-70/hr. Our rate is $20-35/hr — 50% savings.' },
      { question: 'Do your engineers speak Spanish?', answer: 'Yes! Native Spanish speakers plus fluent English. Perfect for LatAm + US projects.' },
      { question: 'How does the timezone work?', answer: 'Morocco GMT+1, CDMX GMT-6. Overlap in late morning CDMX / evening Morocco for daily standups.' },
      { question: 'Are you compliant with Mexican LFPDPPP?', answer: 'Yes. Enterprise security standards, GDPR compliant, aligned with Mexican Federal Data Protection Law.' },
      { question: 'How quickly can you start?', answer: 'Team setup in 2 weeks.' },
      { question: 'What technologies do you use?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, GCP.' },
    ],
    localRate: '$40-70',
    ourRate: '$20-35',
    savings: '50%+',
  },
};
